// Lib
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// Utils
import * as pointLib from '../../../../../common/maths/geometry/point';
import { asObject } from '../../../../../common/utils/immutableHelper';
import measurementsRegistry from '../../../measurementsStore/measurementsRegistry';
import animateScrollIntoView from '../../../../utils/animation/animateScrollIntoView';
import getPointerPositions from '../utils/getPointerPositions';
import { getRotationStyle, getTranslationStyles } from '../../../../utils/cssUtil';
import { isSmallGridSize, scaleToGrid } from '../../../../utils/grid/gridUtils';

// Components
import PointerArrow from './PointerArrow';

// Styles
import './Pointer.scss';

// Used when scrolling the pointer into view
const POINTER_SCROLL_MARGIN = {
    top: 200,
    right: 200,
    bottom: 200,
    left: 340,
};

const getPointerSize = (props) => {
    const { gridSize, large } = props;

    const pointerLargeSize = isSmallGridSize(gridSize) ? 20 : 22;
    const pointerNormalSize = isSmallGridSize(gridSize) ? 16 : 18;
    return large ? pointerLargeSize : pointerNormalSize;
};

const getPointerRectSize = (props) => {
    const pointerSize = getPointerSize(props);

    return {
        width: pointerSize,
        height: pointerSize,
    };
};

class Pointer extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            contentPosition: null,
            arrowRotation: 0,
            isTargetOverflowing: false,
        };
    }

    componentDidMount() {
        this.getPointerPosition(this.props);
    }

    componentWillReceiveProps(nextProps) {
        this.getPointerPosition(nextProps);
    }

    onPointerClick = (event) => {
        const { targetRect } = this.props;

        if (!targetRect) return;

        animateScrollIntoView({
            scrollableParent: measurementsRegistry.getCanvasViewportDomElement(),
            targetRect: asObject(targetRect),
            margins: POINTER_SCROLL_MARGIN,
        });
    };

    getPointerPosition = (props) => {
        const { targetPoint, targetRect, layerRect, gridSize } = props;

        if ((!targetRect && !targetPoint) || !layerRect) return;

        const pointerRect = getPointerRectSize(this.props);

        if (!pointerRect) return;

        const { contentPosition, arrowRotation, isTargetOverflowing } = getPointerPositions({
            targetPoint,
            targetRect: asObject(targetRect),
            layerRect,
            pointerRect,
            margin: scaleToGrid(18, gridSize),
        });

        this.setState({ contentPosition, arrowRotation, isTargetOverflowing });
    };

    render() {
        const {
            children,
            className,
            background = '#f4511c',
            color = '#ffffff',
            layerRect,
            large,
            gridSize,
        } = this.props;

        const { contentPosition, arrowRotation, isTargetOverflowing } = this.state;

        // Due to using absolute positioning, move the contentPosition into the layerRect's space
        const translatedContentPosition = pointLib.reverseTranslate(layerRect, contentPosition);

        const pointerPositionerStyle = getTranslationStyles(translatedContentPosition);

        const pointerStyle = {
            background,
            color,
            // Reverse the rotation
            ...getRotationStyle(-arrowRotation),
        };

        const pointerSize = getPointerSize(this.props);

        // The rendering of this component is slightly complicated due to the animation which slides
        // away from the direction the arrow is pointing.
        // In order to do that with CSS we need to rotate the transform origin and then perform
        // an X translation (pointer-rotator).  But we don't want the text to look rotated, so we
        // apply a reverse translation to the text (Pointer).
        return (
            <div className="pointer-positioner" style={pointerPositionerStyle}>
                <div className="pointer-rotator" style={getRotationStyle(arrowRotation)}>
                    <div className="pointer-slider">
                        <PointerArrow color={background} pointerSize={pointerSize} gridSize={gridSize} />

                        <div
                            className={classNames('Pointer', className, { large, clickable: isTargetOverflowing })}
                            style={pointerStyle}
                            onClick={isTargetOverflowing ? this.onPointerClick : undefined}
                        >
                            <div className="content">{children}</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

Pointer.propTypes = {
    className: PropTypes.string,
    targetPoint: PropTypes.object,
    targetRect: PropTypes.object,
    layerRect: PropTypes.object,
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),

    large: PropTypes.bool,
    color: PropTypes.string,
    background: PropTypes.string,
    gridSize: PropTypes.number,
};

export default Pointer;
